import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  Loader2,
  Trash2,
  Pencil,
  Handshake,
  MoreHorizontal,
  Truck,
  Mail,
  Globe,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PartnerFormDialog } from "@/components/PartnerFormDialog";

const Th = ({ children, className = "" }) => (
  <th className={`whitespace-nowrap px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em] ${className}`}>{children}</th>
);
const Td = ({ children, className = "", ...rest }) => (
  <td className={`whitespace-nowrap px-4 py-3 ${className}`} {...rest}>{children}</td>
);

const TYPE_LABEL = { partner: "Partner", supplier: "Beszállító" };
const PAYMENT_TERMS_LABEL = {
  on_delivery: "Átvételkor",
  transfer_8: "8 nap átutalás",
  transfer_10: "10 nap átutalás",
  transfer_15: "15 nap átutalás",
};

export default function PartnersPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("all");
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/partners");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Betöltés sikertelen.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((p) => {
      if (tab !== "all" && p.partner_type !== tab) return false;
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        (p.short_name || "").toLowerCase().includes(q) ||
        (p.tax_id || "").toLowerCase().includes(q)
      );
    });
  }, [items, search, tab]);

  const onDelete = async (p) => {
    if (!window.confirm(`Törlöd a "${p.name}" partnert?`)) return;
    try {
      await api.delete(`/partners/${p._id}`);
      setItems((prev) => prev.filter((x) => x._id !== p._id));
      toast.success("Partner törölve.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Nem sikerült törölni.");
    }
  };

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <header>
        <div className="flex items-center gap-1.5 text-muted-foreground/80">
          <Handshake className="h-3.5 w-3.5" strokeWidth={2} />
          <span className="text-[10px] font-semibold uppercase tracking-[0.22em]">Kapcsolatok</span>
        </div>
        <h1 className="mt-1.5 text-2xl font-semibold tracking-tight">Partnerek</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Üzleti partnerek és beszállítók nyilvántartása. Beszállítók árlistájához külön nézet érhető el.
        </p>
      </header>

      <section className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]">
        <header className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-muted/40">
              <TabsTrigger value="all" data-testid="filter-all">Mind ({items.length})</TabsTrigger>
              <TabsTrigger value="partner" data-testid="filter-partner">
                Partnerek ({items.filter((p) => p.partner_type === "partner").length})
              </TabsTrigger>
              <TabsTrigger value="supplier" data-testid="filter-supplier">
                Beszállítók ({items.filter((p) => p.partner_type === "supplier").length})
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Keresés…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 w-56 pl-8"
                data-testid="partner-search"
              />
            </div>
            <Button size="sm" onClick={() => setOpenCreate(true)} data-testid="new-partner-button">
              <Plus className="h-4 w-4" /> Új partner
            </Button>
          </div>
        </header>

        {loading ? (
          <div className="flex items-center justify-center px-6 py-14 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Betöltés…
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
            <div className="grid h-10 w-10 place-items-center rounded-full border border-border bg-background">
              <Handshake className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
            </div>
            <p className="text-sm font-medium">{search ? "Nincs találat" : "Nincs rögzített partner"}</p>
            {!search && (
              <Button variant="outline" size="sm" className="mt-2 h-8" onClick={() => setOpenCreate(true)}>
                <Plus className="h-3.5 w-3.5" /> Új partner
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                  <Th>Cégnév</Th>
                  <Th className="hidden sm:table-cell">Rövid név</Th>
                  <Th>Típus</Th>
                  <Th className="hidden lg:table-cell">Adószám</Th>
                  <Th className="hidden md:table-cell">Kapcsolattartók</Th>
                  <Th className="hidden lg:table-cell">Fizetési mód</Th>
                  <Th className="hidden text-center md:table-cell">Rendelhető</Th>
                  <Th className="pr-4 text-right">Műveletek</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p._id} data-testid={`partner-row-${p._id}`}
                    onClick={() => navigate(`/partners/${p._id}`)}
                    className="cursor-pointer border-b border-border last:border-0 transition-colors hover:bg-foreground/[0.025]">
                    <Td><span className="font-medium">{p.name}</span></Td>
                    <Td className="hidden text-muted-foreground sm:table-cell">{p.short_name || "—"}</Td>
                    <Td>
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                        p.partner_type === "supplier"
                          ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
                          : "border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-300"
                      }`}>
                        {p.partner_type === "supplier" && <Truck className="h-3 w-3" />}
                        {TYPE_LABEL[p.partner_type] || p.partner_type}
                      </span>
                    </Td>
                    <Td className="hidden text-muted-foreground font-mono text-[12.5px] lg:table-cell">{p.tax_id || "—"}</Td>
                    <Td className="hidden text-muted-foreground md:table-cell">{(p.contacts || []).length}</Td>
                    <Td className="hidden text-muted-foreground lg:table-cell">{PAYMENT_TERMS_LABEL[p.payment_terms] || "—"}</Td>
                    <Td className="hidden text-center whitespace-nowrap md:table-cell">
                      <OrderableBadge partner={p} />
                    </Td>
                    <Td className="pr-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button aria-label="Műveletek"
                            data-testid={`partner-actions-${p._id}`}
                            className="ml-auto grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground">
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          <DropdownMenuLabel>Műveletek</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setEditing(p)}>
                            <Pencil className="h-4 w-4" /> Szerkesztés
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => onDelete(p)}>
                            <Trash2 className="h-4 w-4" /> Törlés
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <PartnerFormDialog
        open={openCreate || !!editing}
        onOpenChange={(v) => { if (!v) { setOpenCreate(false); setEditing(null); } }}
        partner={editing}
        onSaved={(saved, mode) => {
          if (mode === "create") setItems((prev) => [...prev, saved].sort((a, b) => a.name.localeCompare(b.name)));
          else setItems((prev) => prev.map((s) => (s._id === saved._id ? saved : s)));
        }}
      />
    </div>
  );
}

function OrderableBadge({ partner }) {
  // Non-suppliers are not orderable from this module at all.
  if (partner?.partner_type !== "supplier") {
    return <span className="text-muted-foreground/50">—</span>;
  }
  const hasEmail = !!(partner.order_email && partner.order_email.trim());
  if (hasEmail) {
    return (
      <span
        className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
        title={`Rendelési e-mail: ${partner.order_email}`}
        data-testid="orderable-yes"
      >
        <Mail className="h-3 w-3" /> Rendelhető
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800 dark:bg-amber-500/15 dark:text-amber-300"
      title="Nincs rendelési e-mail cím — csak a beszállító saját weboldalán fogad rendelést."
      data-testid="orderable-no"
    >
      <Globe className="h-3 w-3" /> Csak weboldalon
    </span>
  );
}
